// assets/ss-utils.js
window.SS = window.SS || {};

(function () {
  /*** ---------------------------
   * Cookie helpers (with consent)
   * --------------------------- */
  function setCookie(
    TREAT_AS_ESSENTIAL,
    COOKIE_NAME,
    COOKIE_MAX_AGE_SECS,
    value,
    {
      path = "/",
      domain = undefined,
      sameSite = "Lax",
      secure = undefined
    } = {}
  ) {
    if (value == null) return () => {};

    const hasPreferencesConsent = () => {
      if (window.Shopify?.customerPrivacy?.getTrackingConsent) {
        try {
          const tc = Shopify.customerPrivacy.getTrackingConsent();
          if (tc && typeof tc.preferences === "boolean") return tc.preferences;
        } catch {}
      }
      if (window.Cookiebot?.consent) {
        const c = window.Cookiebot.consent;
        if (typeof c.preferences === "boolean") return c.preferences;
      }
      if (typeof window.OnetrustActiveGroups === "string") {
        return window.OnetrustActiveGroups.split(",").includes("C0003");
      }
      return !!TREAT_AS_ESSENTIAL;
    };

    const doWrite = () => {
      if (TREAT_AS_ESSENTIAL || hasPreferencesConsent()) {
        const parts = [
          `${encodeURIComponent(COOKIE_NAME)}=${encodeURIComponent(String(value))}`,
          `Path=${path}`,
          `Max-Age=${Math.max(0, Number(COOKIE_MAX_AGE_SECS) || 0)}`,
          `SameSite=${sameSite}`
        ];
        if (domain) parts.push(`Domain=${domain}`);
        if (secure === true || (secure == null && location.protocol === "https:")) {
          parts.push("Secure");
        }
        document.cookie = parts.join("; ");
      }
    };

    // Debounce multiple consent events
    let scheduled = false;
    const scheduleWrite = () => {
      if (scheduled) return;
      scheduled = true;
      setTimeout(() => { scheduled = false; doWrite(); }, 0);
    };

    // Initial write
    scheduleWrite();

    // Re-apply when consent changes
    const listeners = [];

    if (window.Shopify?.customerPrivacy?.ON_TRACKING_CONSENT_CHANGED) {
      const evt = Shopify.customerPrivacy.ON_TRACKING_CONSENT_CHANGED;
      const handler = () => scheduleWrite();
      document.addEventListener(evt, handler);
      listeners.push(() => document.removeEventListener(evt, handler));
    }
    if (window.addEventListener) {
      const handler = () => scheduleWrite();
      window.addEventListener("CookiebotOnAccept", handler);
      listeners.push(() => window.removeEventListener("CookiebotOnAccept", handler));
    }
    if (typeof window.OptanonWrapper === "function") {
      const t = setTimeout(() => scheduleWrite(), 0);
      listeners.push(() => clearTimeout(t));
    }

    return function unsubscribe() {
      listeners.forEach(off => { try { off(); } catch {} });
    };
  }

  function getCookie(COOKIE_NAME) {
    if (!COOKIE_NAME || typeof document === "undefined") return null;
    const raw = document.cookie;
    if (!raw) return null;

    const target = String(COOKIE_NAME);
    const pairs = raw.split("; ");

    for (let i = 0; i < pairs.length; i++) {
      const eq = pairs[i].indexOf("=");
      const k = eq >= 0 ? pairs[i].slice(0, eq) : pairs[i];
      const v = eq >= 0 ? pairs[i].slice(eq + 1) : "";
      if (decodeURIComponent(k) === target) {
        try { return decodeURIComponent(v); } catch { return v; }
      }
    }
    return null;
  }

  function eraseCookie(
    COOKIE_NAME,
    { path = "/", domain = undefined, sameSite = "Lax", secure = undefined } = {}
  ) {
    const parts = [
      `${encodeURIComponent(COOKIE_NAME)}=`,
      `Path=${path}`,
      "Max-Age=0",
      "Expires=Thu, 01 Jan 1970 00:00:00 GMT",
      `SameSite=${sameSite}`
    ];
    if (domain) parts.push(`Domain=${domain}`);
    if (secure === true || (secure == null && location.protocol === "https:")) {
      parts.push("Secure");
    }
    document.cookie = parts.join("; ");
  }

  /*** ---------------------------
   * Session helpers (yours)
   * --------------------------- */
  function uuidv4() {
    if (crypto && typeof crypto.randomUUID === "function") return crypto.randomUUID();
    const rnd = (n = 16) =>
      (crypto?.getRandomValues?.(new Uint8Array(1))[0] ?? Math.floor(Math.random() * 256)) % n;
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, ch => {
      const r = rnd(16);
      const v = ch === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  const SESSION_KEY = "ss_session_id";

  function genId() {
    return (window.crypto && crypto.randomUUID) ? crypto.randomUUID() : uuidv4();
  }

  function getOrCreateSessionId() {
    let id = null;
    try { id = sessionStorage.getItem(SESSION_KEY); } catch {}
    if (!id) {
      id = genId();
      try {
        sessionStorage.setItem(SESSION_KEY, id);
        localStorage.removeItem(SESSION_KEY);
      } catch {}
    }
    return id;
  }

  function getFreshSessionId() {
    const id = genId();
    try {
      sessionStorage.setItem(SESSION_KEY, id);
      localStorage.removeItem(SESSION_KEY);
    } catch {}
    return id;
  }

  function getSessionId() {
    let id = null;
    try { id = sessionStorage.getItem(SESSION_KEY); } catch {}
    return id || getFreshSessionId();
  }

  function clearSession() {
    try { sessionStorage.removeItem(SESSION_KEY); } catch {}
    try { localStorage.removeItem(SESSION_KEY); } catch {}
  }

  /*** ---------------------------
   * Customer utils (new)
   * --------------------------- */
  const CUSTOMER_COOKIE_NAME = "ss_customer_airtable_id";
  const CUSTOMER_COOKIE_TREAT_AS_ESSENTIAL = true;
  const CUSTOMER_COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 1 year
  const PROXY_PATH = "/apps/ss/create-airtable-customer";
  const CUSTOMER_CLAIM_CACHE_KEY = "ss_customer_claim_cache_v1";

  // Namespace + state
  window.currentCustomer = window.currentCustomer || {};

  // Safe storage helpers
  const safeGetLS = (k) => { try { return localStorage.getItem(k); } catch { return null; } };
  const safeSetLS = (k, v) => { try { localStorage.setItem(k, v); } catch {} };

  // URL builder
  const buildSignedUrl = (base, params) => {
    const qs = Object.entries(params)
      .filter(([, v]) => v != null && v !== "")
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
      .join("&");
    return `${base}?${qs}`;
  };

  /**
   * Detect Shopify customer info.
   * NOTE: For reliability, set window.SS.SHOPIFY_CUSTOMER inline in theme.liquid (see snippet below).
   * We also try a few fallbacks if present.
   */
  function getShopifyCustomerFromWindow() {
    // Preferred: explicitly set by theme.liquid
    if (window.SS && window.SS.SHOPIFY_CUSTOMER) return window.SS.SHOPIFY_CUSTOMER;

    // Possible fallback via ShopifyAnalytics (not guaranteed)
    const cid = window.ShopifyAnalytics?.meta?.page?.customerId || null;
    if (cid) {
      return { shopifyId: String(cid), email: null, firstName: null, lastName: null, phone: null, airtableId: null };
    }

    return null;
  }

  const normalizeClaimValue = (value) => {
    const text = String(value || "").trim();
    return text || "";
  };

  const buildClaimCacheValue = (airtableId, shopifyCustomer) => [
    normalizeClaimValue(airtableId),
    normalizeClaimValue(shopifyCustomer?.shopifyId),
    normalizeClaimValue(shopifyCustomer?.email).toLowerCase(),
  ].join("|");

  async function claimCustomerIdentity({
    airtableId,
    shopifyCustomer,
  }) {
    const id = normalizeClaimValue(airtableId);
    const shopifyId = normalizeClaimValue(shopifyCustomer?.shopifyId);
    if (!id || !shopifyId) return;

    const cacheValue = buildClaimCacheValue(id, shopifyCustomer);
    if (safeGetLS(CUSTOMER_CLAIM_CACHE_KEY) === cacheValue) return;

    try {
      const r = await fetch(PROXY_PATH, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          airtableId: id,
          customer_id: shopifyId,
          email: shopifyCustomer?.email || undefined,
          first_name: shopifyCustomer?.firstName || undefined,
          last_name: shopifyCustomer?.lastName || undefined,
          phone: shopifyCustomer?.phone || undefined,
        }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const json = await r.json().catch(() => ({}));
      const canonicalId = normalizeClaimValue(json?.airtableId) || id;
      if (canonicalId && canonicalId !== id) {
        window.currentCustomer.ss_customer_airtable_id = canonicalId;
        safeSetLS(CUSTOMER_COOKIE_NAME, canonicalId);
        try {
          if (typeof window.SS.setCookie === "function") {
            window.SS.setCookie(
              CUSTOMER_COOKIE_TREAT_AS_ESSENTIAL,
              CUSTOMER_COOKIE_NAME,
              CUSTOMER_COOKIE_MAX_AGE,
              canonicalId
            );
          }
        } catch {}
      }
      safeSetLS(
        CUSTOMER_CLAIM_CACHE_KEY,
        buildClaimCacheValue(canonicalId, shopifyCustomer)
      );
      return canonicalId;
    } catch (err) {
      console.warn("SS.getCreateVisitorCustomer: identity claim failed", err);
      return id;
    }
  }

  /**
   * Always-async function that guarantees a ss_customer_airtable_id.
   * Returns { ss_customer_airtable_id, shopify_customer_id }
   */
  async function getCreateVisitorCustomer() {
    const SHOPIFY_CUSTOMER = getShopifyCustomerFromWindow();
    const persistedShopifyAirtableId = normalizeClaimValue(SHOPIFY_CUSTOMER?.airtableId) || null;

    // 1) Try cookie -> localStorage (your precedence)
    let customerAirtableId =
      (typeof window.SS.getCookie === "function" && window.SS.getCookie(CUSTOMER_COOKIE_NAME)) ||
      safeGetLS(CUSTOMER_COOKIE_NAME) ||
      null;

    // If present, hydrate + return
    if (customerAirtableId) {
      window.currentCustomer.ss_customer_airtable_id = customerAirtableId;
      if (SHOPIFY_CUSTOMER?.shopifyId) {
        window.currentCustomer = { ...window.currentCustomer, ...SHOPIFY_CUSTOMER };
        customerAirtableId = await claimCustomerIdentity({
          airtableId: customerAirtableId,
          shopifyCustomer: SHOPIFY_CUSTOMER,
        });
        window.currentCustomer.ss_customer_airtable_id = customerAirtableId;
      }
      return {
        ss_customer_airtable_id: customerAirtableId,
        shopify_customer_id: SHOPIFY_CUSTOMER?.shopifyId || null
      };
    }

    // 2) Create/lookup via proxy
    try {
      if (SHOPIFY_CUSTOMER?.shopifyId) {
        // Logged-in: GET with identifiers
        window.currentCustomer = { ...window.currentCustomer, ...SHOPIFY_CUSTOMER };
        const url = buildSignedUrl(PROXY_PATH, {
          customer_id: SHOPIFY_CUSTOMER.shopifyId,
          airtable_id: persistedShopifyAirtableId || undefined,
          email: SHOPIFY_CUSTOMER.email,
          first_name: SHOPIFY_CUSTOMER.firstName,
          last_name: SHOPIFY_CUSTOMER.lastName,
          phone: SHOPIFY_CUSTOMER.phone
        });

        const r = await fetch(url, { method: "GET", credentials: "include" });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const json = await r.json();
        if (json?.airtableId) customerAirtableId = json.airtableId;

      } else {
        // Anonymous: POST {}
        const r = await fetch(PROXY_PATH, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({})
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const json = await r.json();
        if (json?.airtableId) customerAirtableId = json.airtableId;
      }
    } catch (err) {
      console.error("SS.getCreateVisitorCustomer: proxy call failed", err);
      throw new Error("Unable to create or retrieve ss_customer_airtable_id");
    }

    // 3) Persist + hydrate
    if (!customerAirtableId) throw new Error("Proxy did not return airtableId");

    window.currentCustomer.ss_customer_airtable_id = customerAirtableId;
    safeSetLS(CUSTOMER_COOKIE_NAME, customerAirtableId);
    try {
      if (typeof window.SS.setCookie === "function") {
        window.SS.setCookie(
          CUSTOMER_COOKIE_TREAT_AS_ESSENTIAL,
          CUSTOMER_COOKIE_NAME,
          CUSTOMER_COOKIE_MAX_AGE,
          customerAirtableId
        );
      }
    } catch (e) {
      console.log("SS.setCookie error:", e);
    }

    return {
      ss_customer_airtable_id: customerAirtableId,
      shopify_customer_id: SHOPIFY_CUSTOMER?.shopifyId || null
    };
  }

  /*** Exports ***/
  window.SS.setCookie = setCookie;
  window.SS.getCookie = getCookie;
  window.SS.eraseCookie = eraseCookie;

  window.SS.getOrCreateSessionId = getOrCreateSessionId;
  window.SS.getFreshSessionId = getFreshSessionId;
  window.SS.getSessionId = getSessionId;
  window.SS.clearSession = clearSession;

  window.SS.getCreateVisitorCustomer = getCreateVisitorCustomer;
})();
